import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { sizer } from "layout/styles/styled/sizer";
import { TabPanel, TabView } from "primereact/tabview";
import { useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { FeeScheduleComparison } from "../../FeeSchedule/components/FeeScheduleComparison";
import BaseRateFilesTable from "../../PayerRules/components/BaseRateFilesTable";
import { ContractsTable } from "../../PayerRules/components/ContractsTable";
import { ClaimsAnalysisDashboard } from "./ClaimsAnalysisDashboard";
import ClaimsList from "./ClaimsList";

// Sub-tab name constants
const SUBTAB_NAMES = {
  SMART_INSIGHTS: "smart-insights",
  LIBRARY: "library",
  BASE_RATE_FILES: "base-rate-files",
  CLAIMS: "claims",
  CLAIM_ANALYSIS: "claim-analysis",
} as const;

const PermissionMessage = styled.div`
  padding: ${sizer(4)};
  text-align: center;
  color: #6c757d;
  font-size: ${sizer(3.5)};
`;

const ContainerWrapper = styled.div`
  padding: ${sizer(2)};
`;

interface IContractsLibraryProps {
  accountId: string;
  accountName?: string;
  subtab?: string;
}

export function ContractsLibrary({
  accountId,
  accountName,
  subtab,
}: IContractsLibraryProps) {
  const { userInfo } = useUserWithAccount();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Check if user is super admin (for underpayments tab)
  const isSuperAdmin = useMemo(() => {
    return userInfo?.scope === UserRole.SUPER_ADMIN;
  }, [userInfo]);

  // Build available sub-tabs based on permissions
  // Smart Insights is always available to everyone
  const availableSubtabs = useMemo(() => {
    const tabs: any[] = [
      SUBTAB_NAMES.SMART_INSIGHTS,
      SUBTAB_NAMES.LIBRARY,
      SUBTAB_NAMES.BASE_RATE_FILES,
      SUBTAB_NAMES.CLAIMS,
    ];

    if (isSuperAdmin) {
      tabs.push(SUBTAB_NAMES.CLAIM_ANALYSIS);
    }

    return tabs;
  }, [isSuperAdmin]);

  // Get current sub-tab from URL or default to smart-insights
  const currentSubtab =
    (subtab as
      | "smart-insights"
      | "library"
      | "base-rate-files"
      | "claims"
      | "claim-analysis") || SUBTAB_NAMES.SMART_INSIGHTS;
  const activeIndex = availableSubtabs.indexOf(currentSubtab);
  const activeSubtabIndex = activeIndex === -1 ? 0 : activeIndex;

  // Handler to update URL when sub-tab changes
  const handleSubtabChange = (e: { index: number }) => {
    const newSubtab = availableSubtabs[e.index];
    if (newSubtab) {
      navigate(`/hospitals/view/${id}/contracts/${newSubtab}`, {
        replace: true,
      });
    }
  };

  // Build TabPanels array based on permissions
  const tabPanels = [
    <TabPanel key="smart-insights" header="Smart Insights">
      <FeeScheduleComparison accountId={accountId} />
    </TabPanel>,
    <TabPanel key="library" header="Library">
      <ContractsTable accountId={accountId} showAddButton />
    </TabPanel>,
    <TabPanel key="base-rate-files" header="Base Rate Files">
      <BaseRateFilesTable accountId={accountId} />
    </TabPanel>,
    <TabPanel key="claims" header="Claims">
      <ClaimsList accountId={accountId} />
    </TabPanel>,
    ...(isSuperAdmin
      ? [
          <TabPanel key="claim-analysis" header="Claim Analysis">
            <ClaimsAnalysisDashboard accountId={accountId} />
          </TabPanel>,
        ]
      : []),
  ];

  return (
    <ContainerWrapper>
      <TabView activeIndex={activeSubtabIndex} onTabChange={handleSubtabChange}>
        {tabPanels}
      </TabView>
    </ContainerWrapper>
  );
}
